// @ts-check
// ── From lead to client: the chevron strip and its detail panels ──────────────
//
// Part 3b of docs/superpowers/plans/2026-10-14-dashboard-build.md, ported from the approved prototype's funnelCard,
// layoutFunnel and bindFunnelCard (mockups/ultimate-dashboard-v3-2026-10-13.html), organic view only: the coach runs
// no ads yet, so there is no Paid ads / Both selector and no ad costs (build plan items 3 to 5 add them). His rulings
// kept: one chevron strip, words centred, a rate on every seam whatever the sample, no sentences on the card itself
// (each step's history is in its hover), Quiet titles.
//
// The server (coach-dashboard.js `funnel`, _shared/dashboard/funnel.js) counts everything; this only says it.

import { fmt, plural, monthOf, spanWords, rangeLabel, addDay, longDay } from './words.js';
import { escHtml } from '../utils.js';
import { ICON, info } from './tiles.js';
import { hasScribe } from '../nav-access.js';

// Where a coach marks a call: Scribe when they have it, Booked's Booking activity when not (modules/nav-access.js).
const markIn = () => (hasScribe() ? 'Scribe' : 'Booked');

const FL_T = 24, FL_G = 8; // the chevron point's depth, and the gap between two chevrons
const STEPS = ['leads', 'booked', 'showed', 'joined'];

/** The percentage between two counts, like the prototype's pc: "0", "4.5", "38". @param {number} a @param {number} b */
const pc = (a, b) => { const p = (a / b) * 100; return !a ? '0' : p < 10 ? p.toFixed(1) : String(Math.round(p)); };
/** @param {number} a @param {number} b */
const rt = (a, b) => `${pc(a, b)}%`;
const B = (/** @type {string|number} */ x) => `<b>${x}</b>`;
/** @param {number} n @param {string} one @param {string} many */
const lc = (n, one, many) => `${fmt(n)} ${n === 1 ? one : many}`;

/** The date menu, as the prototype lists it (null is a divider). @param {string} today */
export function funnelMenu(today) {
  const lm = addDay(today.slice(0, 8) + '01', -1);
  return [['mtd', 'This month'], ['30', 'Last 30 days'], ['lm', 'Last month', monthOf(lm)], ['ttm', 'Last 12 months'], ['ytd', 'This year'], ['all', 'All time'], null, ['custom', 'Custom dates']];
}

/**
 * How the dates picked are said: after a count ("this month"), on the button, and on the history hover's two rows.
 * @param {{ key: string, from: string, to: string, cmpFrom: string|null, cmpTo: string|null, today: string }} F
 */
export function funnelWords(F) {
  const year = Number(F.today.slice(0, 4));
  const names = { mtd: 'This month', 30: 'Last 30 days', lm: 'Last month', ttm: 'Last 12 months', ytd: 'This year', all: 'All time' };
  // All time counts from the day the records start: said as that day ("since June 1")
  const win = { mtd: 'this month', 30: 'in the last 30 days', lm: 'last month', ttm: 'in the last 12 months', ytd: 'this year', all: `since ${longDay(F.from, year)}` };
  const custom = F.key === 'custom';
  const label = rangeLabel(F.from, F.to, year);
  return {
    pill: custom ? label : /** @type {any} */ (names)[F.key],
    win: custom ? (F.from === F.to ? `on ${label}` : `from ${label}`) : /** @type {any} */ (win)[F.key],
    cur: F.key === 'lm' ? monthOf(F.from) : spanWords(F.from, F.to, year),
    prev: F.cmpFrom && F.cmpTo ? (F.key === 'lm' ? monthOf(F.cmpFrom) : spanWords(F.cmpFrom, F.cmpTo, year)) : null,
    yet: F.to === F.today && F.key !== 'all' ? ' yet' : '',
  };
}

/**
 * The card's HTML.
 * @param {any} F the server's `funnel` @param {Record<string, boolean>} open which step's panel is open (s0..s3)
 */
export function funnelHtml(F, open) {
  const W = funnelWords(F), win = W.win, O = F.now, T = F.timing;
  const lcw = (/** @type {number} */ n, /** @type {string} */ one, /** @type {string} */ many) => lc(n, one, many);
  const became = (/** @type {number} */ n) => (n ? `${lcw(n, 'person', 'people')} became ${n === 1 ? 'a client' : 'clients'} after a call` : 'nobody became a client after a call');

  const overClose = `More people became clients than showed up ${win}: some had their calls before these dates.`;
  // Between two steps: [moved on, before, the rate's name, the counts in a sentence, why there is no rate, why over 100%]
  const cols = [
    { name: 'Conversations', count: O.leads },
    { name: 'Calls booked', count: O.booked, conv: [O.booked, O.leads, 'booking rate',
      `${O.booked ? `${lcw(O.booked, 'call was', 'calls were')} booked` : 'No calls were booked'} ${win}, and ${O.leads ? `${lcw(O.leads, 'new conversation', 'new conversations')} came in` : 'no new conversations came in'}.`,
      O.booked ? `No new conversations came in ${win}. The calls booked came from conversations before these dates.` : `No new conversations came in ${win}${W.yet}.`,
      `More calls were booked than new conversations came in ${win}: some calls came from conversations before these dates.`] },
    { name: 'Showed up', count: O.showed, conv: [O.showed, O.held, 'show rate',
      !O.showed ? `Nobody showed up to the ${lcw(O.held, 'call', 'calls')} that happened ${win}.` : O.held === 1 ? `The 1 call that happened ${win} showed up.` : O.showed === O.held ? `${O.held === 2 ? 'Both' : `All ${fmt(O.held)}`} calls that happened ${win} showed up.` : `${fmt(O.showed)} of the ${fmt(O.held)} calls that happened ${win} showed up.`,
      `No calls have happened ${win}${W.yet}.`, ''] },
    { name: 'Became clients', count: O.joined, conv: [O.joined, O.showed, 'close rate',
      `${became(O.joined)[0].toUpperCase() + became(O.joined).slice(1)}, and ${lcw(O.showed, 'person', 'people')} showed up to a call ${win}.`,
      O.joined ? `Nobody showed up to a call ${win}. These clients had their calls before these dates.` : `Nobody showed up to a call ${win}${W.yet}.`,
      overClose] },
  ];
  /** @param {any} c */
  const pillOf = (c) => (c.conv && c.conv[1] ? (c.conv[0] > c.conv[1] ? 'Over 100%' : `${pc(c.conv[0], c.conv[1])}%`) : '');

  // Each step's history in its hover: the dates picked, the dates before, the last 6 months, the average
  const tops = [W.cur, W.prev];
  const histOf = (/** @type {string} */ title, /** @type {string} */ k, /** @type {number} */ now) => {
    const months = F.months.filter((/** @type {any} */ m) => m.counts && !tops.includes(monthOf(`${m.month}-01`))).map((/** @type {any} */ m) => [monthOf(`${m.month}-01`), fmt(m.counts[k])]);
    const vals = F.months.filter((/** @type {any} */ m) => m.counts).map((/** @type {any} */ m) => m.counts[k]);
    const avg = vals.length >= 2 ? vals.reduce((/** @type {number} */ a, /** @type {number} */ b) => a + b, 0) / vals.length : null;
    const R = { title, top: [[W.cur, fmt(now)], ...(F.before ? [[W.prev, fmt(F.before[k])]] : [])], months,
      avg: avg == null ? null : [`Average a month, last ${vals.length} months`, avg < 10 ? (Math.round(avg * 10) / 10).toString() : fmt(avg)] };
    return `data-hist="${encodeURIComponent(JSON.stringify(R))}"`;
  };

  const isOpen = (/** @type {string} */ k) => !!open[k];
  const more = `<span class="dv-fl-more"><span class="c">See details</span><span class="o">Hide details</span>${ICON.chev}</span>`;
  const stages = cols.map((c, i) => {
    const pill = pillOf(c), k = 's' + i, was = F.before ? F.before[STEPS[i]] : null;
    return `<button type="button" class="dv-fl-st dv-fl-st--${i}${isOpen(k) ? ' is-open' : ''}" data-stage="${k}" data-i="${i}" aria-expanded="${isOpen(k)}" aria-controls="dv-fl-p-${k}" aria-label="${c.name}: ${fmt(c.count)}${was != null ? `, against ${fmt(was)} for ${escHtml(W.prev || '')}` : ''}${pill && c.conv ? `. ${pill} ${c.conv[2]}` : ''}. Show the details" ${histOf(c.name, STEPS[i], c.count)}>
      <svg class="dv-fl-shape" aria-hidden="true"><path class="f"/><path class="r"/></svg>
      <span class="dv-fl-in"><span class="dv-fl-lab">${c.name}</span><span class="dv-fl-num">${fmt(c.count)}</span>
      ${i === 2 ? `<span class="dv-fl-of">of ${lcw(O.held, 'call', 'calls')} that happened</span>` : ''}
      ${c.conv ? `<span class="dv-fl-pct">${pill ? `${B(pill)} ${c.conv[2]}` : `No ${c.conv[2]} yet`}</span>` : ''}
      ${more}</span></button>`;
  }).join('');
  const pills = cols.slice(1).map((c) => {
    const pill = pillOf(c), conv = /** @type {any[]} */ (c.conv);
    const why = pill === 'Over 100%' ? conv[5] : conv[3];
    return pill
      ? `<span class="dv-conv dv-fl-conv" tabindex="0" data-tip="${escHtml(`${pill} ${conv[2]}|${why}`)}" aria-label="${escHtml(`${pill} ${conv[2]}. ${why}`)}">${pill}</span>`
      : `<span class="dv-conv dv-conv--none dv-fl-conv" tabindex="0" data-tip="${escHtml(`No ${conv[2]} yet|${conv[4]}`)}" aria-label="${escHtml(`No ${conv[2]} yet. ${conv[4]}`)}">None</span>`;
  }).join('');

  // ── The panels ──
  const status = (/** @type {'good'|'warn'|'none'} */ kind, /** @type {string} */ words) => {
    const icon = kind === 'warn' ? ICON.warn
      : kind === 'good' ? '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" fill="var(--primary)"/><path d="M8 12.4l2.7 2.7L16.2 9.6" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      : '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-dasharray="3 2.6"/></svg>';
    return `<span class="dv-status${kind === 'none' ? ' dv-status--none' : ''}">${icon}${words}</span>`;
  };
  const fig = (/** @type {string} */ label, /** @type {string} */ val, /** @type {string} */ sub, /** @type {boolean} */ none = false) => `<div class="dv-fl-fig"><div class="dv-fl-fl">${label}</div><div class="dv-fl-fv${none ? ' is-none' : ''}">${val}</div><div class="dv-fl-fs">${sub || ''}</div></div>`;
  const figs = (/** @type {string[]} */ list) => `<div class="dv-fl-figs${list.length > 3 ? ' is-many' : ''}">${list.join('')}</div>`;
  const panel = (/** @type {string} */ k, /** @type {string} */ title, /** @type {string} */ lede, /** @type {string} */ body) => `<div class="dv-fl-panel" data-key="${k}" id="dv-fl-p-${k}" role="region" aria-label="${title}"${isOpen(k) ? '' : ' hidden'}>
      <span class="dv-fl-caret" aria-hidden="true"></span>
      <div class="dv-fl-ph"><div class="dv-fl-pl">${lede}</div><button type="button" class="dv-fl-x" aria-label="Close the details for ${title.toLowerCase()}">${ICON.close}Close</button></div>
      ${body}</div>`;

  // "today at 2:00 PM", "tomorrow at 9:00 AM", "Thursday at 4:30 PM", in the coach's zone
  const nextWords = (() => {
    if (!T.nextCallAt) return 'none booked';
    const at = new Date(T.nextCallAt), tz = F.tz || undefined;
    const day = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
    const time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(at);
    const name = day === F.today ? 'today' : day === addDay(F.today, 1) ? 'tomorrow' : new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'long' }).format(at);
    return `${name} at ${time}`;
  })();
  const figCal = fig('Calls on your calendar', `${fmt(T.next7)} <small>in the next 7 days</small>`, `Next call: ${B(escHtml(nextWords))}`);
  const mins = T.replyMinutes == null ? null : Math.round(T.replyMinutes);
  const reply = mins == null ? null : T.replyMinutes >= 120 ? [String(Math.round((T.replyMinutes / 60) * 10) / 10), 'hours']
    : mins < 1 ? ['Under 1', 'minute'] : [String(mins), mins === 1 ? 'minute' : 'minutes'];
  const waitW = T.waiting ? ` ${B(fmt(T.waiting))} still waiting for your first reply.` : '';
  const replyW = T.replies > 1 ? 'Typically. Half of your first replies were faster than this.' : 'How fast you answered the 1 lead who wrote first.';
  const figReply = reply ? fig('Speed to first reply', `${reply[0]} <small>${reply[1]}</small>`, `${replyW}${waitW}`)
    : fig('Speed to first reply', 'None yet', T.waiting ? `${B(fmt(T.waiting))} new ${T.waiting === 1 ? 'lead is' : 'leads are'} waiting for your first reply` : `No new leads ${win} to reply to`, true);
  const farW = !T.booked ? 'No calls booked yet'
    : T.far ? `${fmt(T.far)} of the ${lcw(T.booked, 'call', 'calls')} ${T.far === 1 ? 'was' : 'were'} booked 4 or more days out`
    : T.booked === 1 ? 'The 1 call was booked less than 4 days out' : `None of the ${fmt(T.booked)} calls were booked 4 or more days out`;
  const figLag = T.lagDays != null ? fig('Days from booking to call', `${Math.round(T.lagDays)} <small>${Math.round(T.lagDays) === 1 ? 'day' : 'days'}</small>`, `${T.booked > 1 ? 'Typically. ' : ''}${farW}`)
    : fig('Days from booking to call', 'None yet', `No calls booked ${win}`, true);
  const figNoShow = fig('No-shows', fmt(O.noShow), !O.held ? 'No calls have happened yet' : O.held === 1 ? 'Of the 1 call that has happened' : `Of ${fmt(O.held)} calls that have happened`);
  const figUnmarked = O.unmarked
    ? fig('Not marked yet', fmt(O.unmarked), status('warn', `${plural(O.unmarked, 'call is', 'calls are')} not marked Showed or No-show. ${O.unmarked === 1 ? `Mark it in ${markIn()}.` : `Mark them on each call in ${markIn()}.`}`))
    : fig('Not marked yet', 'None', O.held ? status('good', 'Every call is marked') : status('none', 'No calls have happened yet'), true);
  const figUpcoming = fig('Still to come', fmt(O.upcoming), 'Booked calls that have not happened yet');
  const figClose = fig('Close rate', !O.showed ? 'Not yet' : O.joined > O.showed ? 'Over 100%' : rt(O.joined, O.showed),
    !O.showed ? `Nobody showed up to a call ${win}${W.yet}` : O.joined > O.showed ? overClose : `${fmt(O.joined)} joined after a call and ${fmt(O.showed)} showed up, ${win}`, !O.showed);
  const figDeciding = fig('Still deciding', fmt(O.deciding), 'Had a call and have not said yes or no yet');
  const figNoCall = fig('Joined without a call', fmt(O.noCall), 'Became clients without a call FitOps knows of');

  const C = F.cameFrom, total = C.total;
  const fromBlock = !total
    ? `<div class="dv-fl-from"><div class="dv-fl-fromh"><b>Where your new clients came from</b></div><p class="dv-fl-fromnone">Nobody joined ${win}${W.yet}.</p></div>`
    : (() => {
      const rows = C.rows.filter((/** @type {any} */ r) => r.count || r.key === 'referral').concat(C.noSource ? [{ name: 'No source yet', key: null, count: C.noSource, referrers: [] }] : []);
      const top = Math.max(1, ...rows.map((/** @type {any} */ r) => r.count));
      const by = (/** @type {any[]} */ xs) => xs.map((x) => (x.count > 1 ? `${x.name} (${x.count})` : x.name)).join(', ');
      return `<div class="dv-fl-from"><div class="dv-fl-fromh"><b>Where your new clients came from</b><span>${total === 1 ? 'The 1 client' : `All ${fmt(total)} clients`} who joined ${win}, with or without a call</span></div>
        <div class="dv-fl-fromrows">${rows.map((/** @type {any} */ r) => `<div class="dv-fl-fromrow"><span class="l">${escHtml(r.name)}${r.referrers.length ? `<em>Sent by ${escHtml(by(r.referrers))}</em>` : ''}</span><i style="--w:${(r.count / top) * 100}%"></i><b>${fmt(r.count)}</b></div>`).join('')}</div></div>`;
    })();

  const convoLede = O.leads ? `${B(fmt(O.leads))} ${O.leads === 1 ? 'conversation' : 'conversations'} ${win}: ${B(fmt(O.inbound))} started by the lead and ${B(fmt(O.leads - O.inbound))} by you.` : `No new conversations ${win}${W.yet}.`;
  const bookLede = `${O.booked ? `${B(fmt(O.booked))} ${O.booked === 1 ? 'call was' : 'calls were'} booked ${win}` : `No calls were booked ${win}`}, and ${O.leads ? `${B(fmt(O.leads))} ${O.leads === 1 ? 'new conversation' : 'new conversations'} came in` : 'no new conversations came in'}.`;
  const showLede = (O.held
    ? (O.held === 1 && O.showed === 1 ? `The 1 call that happened ${win} showed up.`
      : O.showed === O.held && O.held > 1 ? `${O.held === 2 ? 'Both' : `All ${B(fmt(O.held))}`} calls that happened ${win} showed up.`
      : O.showed ? `${B(fmt(O.showed))} of the ${B(fmt(O.held))} ${O.held === 1 ? 'call' : 'calls'} that happened ${win} showed up.`
      : `Nobody showed up to the ${B(fmt(O.held))} ${O.held === 1 ? 'call' : 'calls'} that happened ${win}.`)
    : `No calls have happened ${win}${W.yet}.`) + (O.upcoming ? ` ${B(fmt(O.upcoming))} more ${O.upcoming === 1 ? 'is' : 'are'} still to come.` : '');
  const joinLede = (O.joined ? `${B(fmt(O.joined))} ${O.joined === 1 ? 'person' : 'people'} from your conversations joined after a call ${win}.` : `Nobody from your conversations has joined after a call ${win}${W.yet}.`)
    + (O.deciding ? ` ${B(fmt(O.deciding))} ${O.deciding === 1 ? 'person is' : 'people are'} still deciding.` : '');

  const panels = panel('s0', 'Conversations', convoLede, figs([
      fig('They messaged first', fmt(O.inbound), O.leads ? `${rt(O.inbound, O.leads)} of conversations` : ''),
      fig('You messaged first', fmt(O.leads - O.inbound), O.leads ? `${rt(O.leads - O.inbound, O.leads)} of conversations` : ''),
      figReply,
    ].concat(F.before ? [fig(`${escHtml(W.prev || '')}, to compare`, fmt(F.before.leads), F.before.leads === 1 ? 'New conversation' : 'New conversations')] : [])))
    + panel('s1', 'Calls booked', bookLede, figs([figCal, figReply, figLag]))
    + panel('s2', 'Showed up', showLede, figs([figNoShow, figUnmarked, figUpcoming]))
    + panel('s3', 'Became clients', joinLede, figs([figClose, figDeciding, figNoCall]) + fromBlock);

  const head = `<div class="dv-card-head">
      <div style="display:flex;align-items:center;gap:6px"><h2 class="dv-title" id="dv-fl-h">From lead to client</h2>${info('flow')}</div>
      <div class="dv-head-tools"><button type="button" class="dv-pick" data-menu="fl" aria-haspopup="menu" aria-expanded="false" aria-label="Dates: ${escHtml(W.pill)}. Change them" title="${escHtml(W.pill)}"><b>${escHtml(W.pill)}</b>${ICON.chev}</button></div>
    </div>`;
  return `<section class="dv-card dv-fl" aria-labelledby="dv-fl-h">
    ${head}
    <div class="dv-fl-strip" role="group" aria-label="From lead to client, ${escHtml(win)}. Choose a step to see its details.">
      <div class="dv-fl-chevs">${stages}${pills}</div>
      ${panels}
    </div>
  </section>`;
}

/** A rounded polygon: every corner gets its own radius. @param {number[][]} pts */
function roundPoly(pts) {
  let d = '';
  pts.forEach(([x, y, r], i) => {
    const [px, py] = pts[(i - 1 + pts.length) % pts.length], [nx, ny] = pts[(i + 1) % pts.length];
    const l1 = Math.hypot(px - x, py - y), l2 = Math.hypot(nx - x, ny - y), r1 = Math.min(r, l1 / 2), r2 = Math.min(r, l2 / 2);
    const ax = x + ((px - x) * r1) / l1, ay = y + ((py - y) * r1) / l1, bx = x + ((nx - x) * r2) / l2, by = y + ((ny - y) * r2) / l2;
    d += `${i ? ' L' : 'M'}${ax.toFixed(1)},${ay.toFixed(1)} Q${x},${y} ${bx.toFixed(1)},${by.toFixed(1)}`;
  });
  return d + ' Z';
}

/** One chevron's outline. @param {number} w @param {number} h @param {number} i @param {number} last @param {number} o */
function flShape(w, h, i, last, o) {
  const pts = [[-o, -o, (i ? 6 : 16) + o], [i < last ? w - FL_T + o * 0.4 : w + o, -o, (i < last ? 6 : 16) + o]];
  if (i < last) pts.push([w + o * 1.1, h / 2, 10 + o]);
  pts.push([i < last ? w - FL_T + o * 0.4 : w + o, h + o, (i < last ? 6 : 16) + o], [-o, h + o, (i ? 6 : 16) + o]);
  if (i) pts.push([FL_T - o * 1.1, h / 2, Math.max(2, 8 - o)]);
  return roundPoly(pts);
}

/** The open panel's pointer, under its step. @param {HTMLElement} root */
function placeCaret(root) {
  const p = /** @type {HTMLElement|null} */ (root.querySelector('.dv-fl-panel:not([hidden])'));
  if (!p) return;
  const caret = /** @type {HTMLElement|null} */ (p.querySelector('.dv-fl-caret'));
  const btn = /** @type {HTMLElement|null} */ (root.querySelector(`[data-stage="${p.dataset.key}"]`));
  if (!btn || !caret) return;
  if (root.classList.contains('dv-fl--narrow')) { caret.style.left = ''; return; }
  const pr = p.getBoundingClientRect(), br = btn.getBoundingClientRect();
  let a = br.left, b = br.right;
  const i = Number(btn.dataset.i), last = root.querySelectorAll('.dv-fl-st').length - 1;
  if (i) a += FL_T;
  if (i < last) b -= FL_T;
  caret.style.left = Math.round((a + b) / 2 - pr.left) + 'px';
}

/**
 * Cut each chevron at its real size, put each rate on its seam level with the numbers, and pick the wide or narrow
 * arrangement. Runs after every draw and when the card's width changes.
 * @param {HTMLElement|null} root the .dv-fl card
 */
export function layoutFunnel(root) {
  if (!root) return;
  const strip = /** @type {HTMLElement|null} */ (root.querySelector('.dv-fl-strip'));
  if (!strip || !strip.clientWidth) return; // a hidden page has no width; laid out when it shows
  const chevs = /** @type {HTMLElement} */ (strip.querySelector('.dv-fl-chevs'));
  const cards = /** @type {HTMLElement[]} */ ([...strip.querySelectorAll('.dv-fl-st')]), last = cards.length - 1;
  const narrow = strip.clientWidth < 600;
  root.classList.toggle('dv-fl--narrow', narrow);
  root.classList.toggle('dv-fl--wide', !narrow);
  cards.forEach((el) => { el.style.width = el.style.height = el.style.marginLeft = el.style.paddingLeft = el.style.paddingRight = ''; });
  if (!narrow) {
    const w = (chevs.clientWidth + last * (FL_T - FL_G)) / cards.length;
    cards.forEach((el, i) => {
      el.style.width = w + 'px';
      el.style.marginLeft = i ? -(FL_T - FL_G) + 'px' : '0';
      // Centred words keep the same room from the notch and from the point
      el.style.paddingLeft = (i ? FL_T + 14 : 16) + 'px';
      el.style.paddingRight = (i < last ? FL_T + 14 : 16) + 'px';
    });
    let ih = 0;
    cards.forEach((el) => { ih = Math.max(ih, /** @type {HTMLElement} */ (el.querySelector('.dv-fl-in')).offsetHeight); });
    const h = Math.max(128, Math.ceil(ih + 44));
    cards.forEach((el, i) => {
      el.style.height = h + 'px';
      const svg = /** @type {SVGSVGElement} */ (/** @type {unknown} */ (el.querySelector('.dv-fl-shape')));
      svg.setAttribute('width', String(w)); svg.setAttribute('height', String(h)); svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
      /** @type {Element} */ (svg.querySelector('.f')).setAttribute('d', flShape(w, h, i, last, 0));
      /** @type {Element} */ (svg.querySelector('.r')).setAttribute('d', flShape(w, h, i, last, 3));
    });
    // Each rate on its seam, level with the numbers, sliding along the slanted seam so it stays in the gap (10/13)
    const num = /** @type {HTMLElement} */ (cards[0].querySelector('.dv-fl-num'));
    const yN = /** @type {HTMLElement} */ (num.parentElement).offsetTop + num.offsetTop + num.offsetHeight / 2;
    chevs.querySelectorAll('.dv-fl-conv').forEach((el, i) => {
      const p = /** @type {HTMLElement} */ (el);
      p.style.left = (i * (w - FL_T + FL_G) + w - FL_T + FL_G / 2 + (FL_T * 2 * yN) / h) + 'px';
      p.style.top = yN + 'px';
    });
  }
  placeCaret(root);
}

/**
 * One step open at a time: click, Enter or Space opens; arrows move; Escape and Close shut it.
 * @param {HTMLElement|null} root @param {Record<string, boolean>} open shared with the draw, so a redraw keeps it
 */
export function bindFunnelCard(root, open) {
  if (!root) return;
  const setOpen = (/** @type {string|null} */ key) => {
    Object.keys(open).forEach((k) => { delete open[k]; });
    if (key) open[key] = true;
    root.querySelectorAll('[data-stage]').forEach((el) => { const b = /** @type {HTMLElement} */ (el), on = b.dataset.stage === key; b.classList.toggle('is-open', on); b.setAttribute('aria-expanded', String(on)); });
    root.querySelectorAll('.dv-fl-panel').forEach((el) => { const p = /** @type {HTMLElement} */ (el); p.hidden = p.dataset.key !== key; });
    placeCaret(root);
  };
  const current = () => Object.keys(open).find((k) => open[k]) || null;
  root.addEventListener('click', (e) => {
    const t = /** @type {HTMLElement} */ (e.target);
    if (t.closest('.dv-fl-x')) {
      const k = current(); setOpen(null);
      /** @type {HTMLElement|null} */ (root.querySelector(`[data-stage="${k}"]`))?.focus();
      return;
    }
    const b = /** @type {HTMLElement|null} */ (t.closest('[data-stage]'));
    if (!b || !root.contains(b)) return;
    setOpen(current() === b.dataset.stage ? null : b.dataset.stage || null);
  });
  root.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && current()) {
      const k = current(); setOpen(null);
      /** @type {HTMLElement|null} */ (root.querySelector(`[data-stage="${k}"]`))?.focus();
      e.preventDefault(); return;
    }
    const b = /** @type {HTMLElement|null} */ (/** @type {HTMLElement} */ (e.target).closest && /** @type {HTMLElement} */ (e.target).closest('[data-stage]'));
    if (!b) return;
    const all = /** @type {HTMLElement[]} */ ([...root.querySelectorAll('[data-stage]')]), i = all.indexOf(b);
    let j = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = Math.min(all.length - 1, i + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = Math.max(0, i - 1);
    else if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = all.length - 1;
    if (j >= 0) { all[j].focus(); e.preventDefault(); }
  });
}

/**
 * The step history hovers (data-hist) and the rate bubbles' hovers (data-tip="Title|Detail").
 * @param {HTMLElement|null} root @param {HTMLElement} tip
 */
export function bindFunnelTips(root, tip) {
  if (!root) return;
  const place = (/** @type {HTMLElement} */ el) => {
    const r = el.getBoundingClientRect();
    tip.classList.remove('dv-tip--calc'); // a box's working-out hover left its two-column layout (final review, 10/15)
    tip.classList.add('on');
    tip.style.left = '0px';
    const tw = tip.offsetWidth, th = tip.offsetHeight;
    tip.style.left = Math.max(8, Math.min(window.innerWidth - tw - 8, r.left + r.width / 2 - tw / 2)) + 'px';
    tip.style.top = (r.top - th - 8 >= 8 ? r.top - th - 8 : r.bottom + 8) + 'px';
  };
  const hide = () => { tip.classList.remove('on'); tip.dataset.for = ''; };
  root.querySelectorAll('[data-hist]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    const show = () => {
      if (tip.classList.contains('on') && tip.dataset.for === el.dataset.stage) return;
      const R = JSON.parse(decodeURIComponent(el.dataset.hist || '%7B%7D'));
      const row = (/** @type {string[]} */ [l, v]) => `<i></i><span>${escHtml(l)}</span><em>${escHtml(v)}</em>`;
      tip.innerHTML = `<b class="dv-tip-head">${escHtml(R.title)}</b><div class="dv-tip-rows">${R.top.map(row).join('')}${R.months.length ? `<i class="dv-tip-gap"></i>${R.months.map(row).join('')}` : ''}${R.avg ? `<i class="dv-tip-rule"></i><i></i><span>${escHtml(R.avg[0])}</span><em>${escHtml(R.avg[1])}</em>` : ''}</div>`;
      tip.dataset.for = el.dataset.stage || '';
      place(el);
    };
    el.addEventListener('mouseenter', show); el.addEventListener('mousemove', show); el.addEventListener('focus', show);
    el.addEventListener('mouseleave', hide); el.addEventListener('blur', hide);
  });
  root.querySelectorAll('[data-tip]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    const show = () => {
      const [title, detail] = String(el.dataset.tip || '').split('|');
      tip.innerHTML = `<b>${escHtml(title)}</b><span>${escHtml(detail || '')}</span>`;
      tip.dataset.for = '';
      place(el);
    };
    el.addEventListener('mouseenter', show); el.addEventListener('focus', show);
    el.addEventListener('mouseleave', hide); el.addEventListener('blur', hide);
  });
}

